import React from 'react';
import { Dialog } from '@headlessui/react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Modal } from '@/components/ui';
import { Button } from '@/components/ui';
import { Sparkles } from 'lucide-react';
import { selectTrialInfo } from '@/features/billing/redux/trial-selectors';
import { useAuth } from '@/shared/hooks/useAuth';
import { rutaDePlanes } from '@/features/billing/utils/ruta-de-planes';

export const STORAGE_KEY = 'trial-activated-modal-shown';

interface TrialActivatedModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const TrialActivatedModal: React.FC<TrialActivatedModalProps> = ({
  isOpen,
  onClose,
}) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const trial = useSelector(selectTrialInfo);
  // GH#1679 · 17.3: el texto decía «7 días» fijo; la prueba es de 14 (política
  // STANDARD_TRIAL). Se usa lo que devuelve el backend; sin dato, no se inventa un número.
  const dias = trial.isInTrial && trial.daysRemaining > 0 ? trial.daysRemaining : null;

  const handleClose = () => {
    // Guardar en localStorage que ya se mostró el modal
    if (typeof window !== 'undefined') {
      localStorage.setItem(STORAGE_KEY, 'true');
    }
    onClose();
  };

  // «Elegir plan» antes sólo cerraba el modal.
  const handleElegirPlan = () => {
    handleClose();
    navigate(rutaDePlanes(user?.role));
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      size="lg"
      closeOnOverlayClick={false}
      showCloseButton={true}
      className="max-w-2xl"
    >
      <div className="text-center">
        {/* Preview/Image placeholder - similar to Alegra */}
        <div className="mb-6 rounded-lg bg-gradient-to-br from-blue-50 to-indigo-50 p-4 border border-blue-100">
          <div className="aspect-video bg-white rounded-lg shadow-sm flex items-center justify-center">
            <div className="text-center">
              <div className="w-16 h-16 bg-gradient-to-br from-primary-500 to-primary-600 rounded-full mx-auto mb-3 flex items-center justify-center">
                <Sparkles className="w-8 h-8 text-white" />
              </div>
              <p className="text-sm text-gray-600 font-medium">
                Vista previa del dashboard
              </p>
            </div>
          </div>
        </div>

        {/* Title. Dialog.Title (no un h2 suelto): headlessui lo enlaza con aria-labelledby
            del diálogo. Sin eso el lector de pantalla anuncia un diálogo SIN nombre, porque
            Modal solo enlaza el título cuando recibe la prop `title`. */}
        <Dialog.Title as="h2" className="text-2xl font-bold text-gray-900 mb-3">
          {dias
            ? `¡Disfruta ${dias} ${dias === 1 ? 'día' : 'días'} de prueba gratis! ✨`
            : '¡Tu prueba gratis está activa! ✨'}
        </Dialog.Title>

        {/* Body Text */}
        <p className="text-gray-600 mb-8 leading-relaxed">
          Explora las funciones avanzadas y elige el plan ideal para hacer
          crecer tu negocio.
        </p>

        {/* Buttons */}
        <div className="flex flex-col sm:flex-row gap-3 justify-center">
          <Button
            variant="primary"
            size="lg"
            onClick={handleClose}
            className="bg-teal-600 hover:bg-teal-700 text-white px-8"
          >
            Continuar probando
          </Button>
          <Button
            variant="outline"
            size="lg"
            onClick={handleElegirPlan}
            className="border-teal-600 text-teal-600 hover:bg-teal-50 px-8"
          >
            Elegir plan
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default TrialActivatedModal;
